import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Database,
  Hash,
  Layers,
  Loader2,
  Search,
  Sparkles,
  Type,
} from 'lucide-react';
import { analyticsDeclarationPractice } from '../../services/api';
import HSCode from '../common/HSCode';

const fmtN = (value) => value == null ? '—' : Number(value).toLocaleString('uk-UA');
const fmtPct = (value) => value == null ? '—' : `${(Number(value) * 100).toFixed(1)}%`;
const fmtPrice = (value) => value == null ? '—' : `$${Number(value).toFixed(2)}`;

const MODES = [
  { id: 'query', label: 'За описом', Icon: Type },
  { id: 'item', label: 'Від декларації', Icon: Sparkles },
  { id: 'code', label: 'За кодом', Icon: Hash },
];

const SCOPES = [
  { id: 'strict', label: 'Строго' },
  { id: 'balanced', label: 'Баланс' },
  { id: 'wide', label: 'Широко' },
];
const ALL_PERIOD_FROM = '1900-01-01';

const WARNING_LABELS = {
  small_sample: 'мало різних оформлень',
  small_pool: 'мала вибірка',
  possible_batch_dominance: 'схоже на один батч',
  outdated_practice: 'мало свіжих декларацій',
  duplicate_cluster_dominates: 'великий повторюваний кластер',
  importer_dominates: 'домінує один імпортер',
};

function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

function yearAgoInput() {
  const d = new Date();
  d.setDate(d.getDate() - 365);
  return d.toISOString().slice(0, 10);
}

function cleanCode(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 10);
}

function Field({ label, children }) {
  return (
    <label className="flex min-w-0 flex-col text-xs font-semibold uppercase text-text-secondary">
      {label}
      {children}
    </label>
  );
}

function Stat({ label, value, sub, tone = 'default' }) {
  const colors = {
    default: 'border-border-default bg-surface text-text-primary',
    blue: 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-200',
    green: 'border-green-200 bg-green-50 text-green-900 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-200',
    amber: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${colors[tone] || colors.default}`}>
      <div className="text-[11px] font-semibold uppercase opacity-70">{label}</div>
      <div className="mt-0.5 text-xl font-bold">{value}</div>
      {sub && <div className="text-[11px] opacity-70">{sub}</div>}
    </div>
  );
}

function SliceList({ title, rows, keyName, valueName = 'n' }) {
  if (!rows?.length) return null;
  const max = Math.max(...rows.map(r => Number(r[valueName] || 0)), 1);
  return (
    <div className="rounded-lg border border-border-default bg-surface p-3">
      <h3 className="mb-2 text-sm font-bold text-text-primary">{title}</h3>
      <div className="space-y-1.5">
        {rows.slice(0, 8).map((row, idx) => {
          const label = row[keyName] || '—';
          const n = Number(row[valueName] || 0);
          return (
            <div key={`${label}-${idx}`} className="grid grid-cols-[minmax(0,1fr)_72px] items-center gap-2 text-xs">
              <div className="min-w-0">
                <div className="truncate text-text-secondary" title={String(label)}>{label}</div>
                <div className="mt-0.5 h-1.5 rounded-full bg-app">
                  <div className="h-1.5 rounded-full bg-blue-300 dark:bg-blue-800" style={{ width: `${Math.max(4, (n / max) * 100)}%` }} />
                </div>
              </div>
              <div className="text-right font-mono text-text-muted">{fmtN(n)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AnchorCard({ anchor }) {
  const declaration = anchor?.declaration;
  if (!declaration) return null;
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-200">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">Якір #{declaration.original_id}</span>
        <HSCode code={declaration.product_code} size="sm" showCopyAlways />
        {declaration.trademark && <span>ТМ {declaration.trademark}</span>}
        {declaration.country_of_origin && <span>{declaration.country_of_origin}</span>}
        {(declaration.declaration_date || declaration.declaration_year) && (
          <span>{declaration.declaration_date || declaration.declaration_year}</span>
        )}
      </div>
      {declaration.description && (
        <div className="mt-1 line-clamp-2 text-blue-800 dark:text-blue-200/80">
          {declaration.description}
        </div>
      )}
    </div>
  );
}

function CodeTable({ rows, selectedCode, onSelect }) {
  if (!rows?.length) return null;
  return (
    <div className="overflow-hidden rounded-lg border border-border-default bg-surface">
      <div className="border-b border-border-default px-3 py-2">
        <h3 className="text-sm font-bold text-text-primary">Коди в практиці оформлення</h3>
      </div>
      <div className="max-h-[520px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-app text-[11px] uppercase text-text-disabled">
            <tr>
              <th className="px-3 py-2 text-left">Код</th>
              <th className="px-3 py-2 text-right">Декл.</th>
              <th className="px-3 py-2 text-right">Частка декл.</th>
              <th className="px-3 py-2 text-right">Різні</th>
              <th className="px-3 py-2 text-right">Частка різних</th>
              <th className="px-3 py-2 text-right">Без дати</th>
              <th className="px-3 py-2 text-right">У БД</th>
              <th className="px-3 py-2 text-right">Свіжі</th>
              <th className="px-3 py-2 text-left">Марки / країни</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const selected = selectedCode === row.product_code;
              const topTrademark = row.top_trademarks?.[0]?.trademark;
              const topCountry = row.top_countries?.[0]?.country;
              return (
                <tr
                  key={row.product_code}
                  onClick={() => onSelect(row.product_code)}
                  className={`cursor-pointer border-b border-border-default transition-colors ${
                    selected ? 'bg-blue-50 dark:bg-blue-950/30' : 'hover:bg-hover'
                  }`}
                >
                  <td className="px-3 py-2"><HSCode code={row.product_code} size="sm" showCopyAlways /></td>
                  <td className="px-3 py-2 text-right font-mono text-text-primary">{fmtN(row.pool_raw_count)}</td>
                  <td className="px-3 py-2 text-right font-mono text-text-primary">{fmtPct(row.pool_raw_share)}</td>
                  <td className="px-3 py-2 text-right font-mono text-text-muted">{fmtN(row.pool_cluster_count)}</td>
                  <td className="px-3 py-2 text-right font-mono text-text-muted">{fmtPct(row.pool_cluster_share)}</td>
                  <td className="px-3 py-2 text-right font-mono text-text-muted">{fmtN(row.pool_undated_count)}</td>
                  <td className="px-3 py-2 text-right font-mono text-text-muted">{fmtN(row.db_raw_count)}</td>
                  <td className="px-3 py-2 text-right font-mono text-text-muted">{fmtN(row.db_recent_count)}</td>
                  <td className="px-3 py-2 text-text-muted">
                    <div className="max-w-[260px] truncate" title={[topTrademark, topCountry].filter(Boolean).join(' · ')}>
                      {[topTrademark, topCountry].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Samples({ codeRow }) {
  if (!codeRow?.samples?.length) return null;
  return (
    <div className="rounded-lg border border-border-default bg-surface p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-text-primary">Приклади по коду</h3>
        <HSCode code={codeRow.product_code} size="sm" />
      </div>
      <div className="space-y-2">
        {codeRow.samples.map(sample => (
          <div key={sample.original_id} className="rounded-lg border border-border-default bg-app p-2.5">
            <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-text-muted">
              <span className="font-mono">#{sample.original_id}</span>
              {sample.declaration_date && <span>{sample.declaration_date}</span>}
              {sample.country && <span>{sample.country}</span>}
              {sample.trademark && <span>ТМ {sample.trademark}</span>}
              {sample.price_per_kg_usd != null && <span>{fmtPrice(sample.price_per_kg_usd)}/кг</span>}
            </div>
            <div className="line-clamp-3 text-xs leading-relaxed text-text-secondary">
              {sample.description}
            </div>
            {(sample.importer_name || sample.declarant_name) && (
              <div className="mt-1 truncate text-[11px] text-text-disabled" title={sample.importer_name || sample.declarant_name}>
                {sample.importer_name || sample.declarant_name}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DeclarationPracticeTab({
  initialAnchorId = null,
  initialQuery = '',
  initialCodePrefix = '',
}) {
  const initialMode = initialAnchorId != null ? 'item' : (initialQuery ? 'query' : 'code');
  const [mode, setMode] = useState(initialMode);
  const [anchorId, setAnchorId] = useState(initialAnchorId);
  const [query, setQuery] = useState(initialQuery);
  const [codePrefix, setCodePrefix] = useState(cleanCode(initialCodePrefix));
  const [country, setCountry] = useState('');
  const [trademark, setTrademark] = useState('');
  const [dateFrom, setDateFrom] = useState(yearAgoInput());
  const [dateTo, setDateTo] = useState(todayInput());
  const [scope, setScope] = useState('balanced');
  const [includeSamples, setIncludeSamples] = useState(true);
  const [includeUndated, setIncludeUndated] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const cacheRef = useRef(new Map());

  const cacheKey = useCallback((targetMode) => JSON.stringify({
    mode: targetMode,
    anchorId: targetMode === 'item' ? anchorId : null,
    query: targetMode === 'query' ? query.trim() : '',
    codePrefix: cleanCode(codePrefix),
    country: country.trim(),
    trademark: trademark.trim(),
    dateFrom,
    dateTo,
    scope,
    includeSamples,
    includeUndated,
  }), [anchorId, query, codePrefix, country, trademark, dateFrom, dateTo, scope, includeSamples, includeUndated]);

  const canLoad = useMemo(() => {
    if (mode === 'item') return anchorId != null;
    if (mode === 'query') return Boolean(query.trim());
    return cleanCode(codePrefix).length >= 2;
  }, [mode, anchorId, query, codePrefix]);

  const load = useCallback(async (targetMode = mode) => {
    const normalizedCode = cleanCode(codePrefix);
    if (targetMode === 'item' && anchorId == null) {
      setError('Обери декларацію з пошуку');
      return;
    }
    if (targetMode === 'query' && !query.trim()) {
      setError('Введи опис товару');
      return;
    }
    if (targetMode === 'code' && normalizedCode.length < 2) {
      setError('Введи код або префікс УКТЗЕД');
      return;
    }

    const key = cacheKey(targetMode);
    if (cacheRef.current.has(key)) {
      setData(cacheRef.current.get(key));
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await analyticsDeclarationPractice({
        anchorId: targetMode === 'item' ? anchorId : undefined,
        query: targetMode === 'query' ? query.trim() : undefined,
        codePrefix: normalizedCode || undefined,
        country: country.trim() || undefined,
        trademark: trademark.trim() || undefined,
        dateFrom,
        dateTo,
        scope,
        includeSamples,
        includeUndated,
      });
      cacheRef.current.set(key, response);
      setData(response);
    } catch (err) {
      setData(null);
      setError(err?.message || 'Не вдалося завантажити практику оформлення');
    } finally {
      setLoading(false);
    }
  }, [mode, anchorId, query, codePrefix, country, trademark, dateFrom, dateTo, scope, includeSamples, includeUndated, cacheKey]);

  useEffect(() => {
    if (canLoad) load(initialMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const first = data?.by_code?.[0]?.product_code;
    setSelectedCode(prev => (prev && data?.by_code?.some(row => row.product_code === prev)) ? prev : first || null);
  }, [data]);

  const selectedRow = useMemo(
    () => data?.by_code?.find(row => row.product_code === selectedCode) || data?.by_code?.[0] || null,
    [data, selectedCode]
  );

  const warnings = data?.quality?.warnings || [];
  const sample = data?.sample || {};
  const signals = data?.signals || {};
  const byCode = data?.by_code || [];

  return (
    <div className="mx-auto max-w-screen-2xl space-y-4 p-4">
      <div className="rounded-xl border border-border-default bg-surface p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-bold text-text-primary">
              <Layers className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              Практика оформлення
            </h2>
            <div className="mt-1 text-xs text-text-muted">
              Частота в базі не підтверджує правильність коду.
            </div>
          </div>
          {data?.quality?.recency_year_min && (
            <div className="rounded-lg border border-border-default bg-app px-3 py-1.5 text-xs text-text-muted">
              Свіжі: з {data.quality.recency_year_min}
            </div>
          )}
        </div>

        <div className="mb-3 grid grid-cols-3 gap-2">
          {MODES.map(item => {
            const Icon = item.Icon;
            const active = mode === item.id;
            const disabled = item.id === 'item' && anchorId == null;
            return (
              <button
                key={item.id}
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  setMode(item.id);
                  const ready =
                    (item.id === 'item' && anchorId != null) ||
                    (item.id === 'query' && query.trim()) ||
                    (item.id === 'code' && cleanCode(codePrefix).length >= 2);
                  if (ready) load(item.id);
                }}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                  active
                    ? 'border-blue-500 bg-blue-50 text-blue-800 dark:border-blue-500 dark:bg-blue-950/40 dark:text-blue-200'
                    : disabled
                      ? 'border-border-default bg-app text-text-disabled'
                      : 'border-border-default bg-surface text-text-secondary hover:bg-hover hover:text-text-primary'
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </button>
            );
          })}
        </div>

        {mode === 'item' && <AnchorCard anchor={data?.anchor} />}

        <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-8">
          {mode === 'query' && (
            <Field label="Опис">
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="mt-1 rounded-lg border border-border-default bg-app px-3 py-2 text-sm text-text-primary"
                placeholder="kinder joy"
              />
            </Field>
          )}
          {mode === 'item' && (
            <Field label="ID декларації">
              <input
                value={anchorId ?? ''}
                onChange={e => setAnchorId(e.target.value ? Number(e.target.value) : null)}
                className="mt-1 rounded-lg border border-border-default bg-app px-3 py-2 text-sm text-text-primary"
                inputMode="numeric"
              />
            </Field>
          )}
          <Field label={mode === 'code' ? 'Код УКТЗЕД' : 'Фільтр коду'}>
            <input
              value={codePrefix}
              onChange={e => setCodePrefix(cleanCode(e.target.value))}
              className="mt-1 rounded-lg border border-border-default bg-app px-3 py-2 text-sm text-text-primary"
              placeholder={mode === 'code' ? '8544' : 'опц.'}
              inputMode="numeric"
            />
          </Field>
          <Field label="Країна">
            <input
              value={country}
              onChange={e => setCountry(e.target.value.toUpperCase())}
              className="mt-1 rounded-lg border border-border-default bg-app px-3 py-2 text-sm text-text-primary"
              placeholder="КИТАЙ"
            />
          </Field>
          <Field label="Марка">
            <input
              value={trademark}
              onChange={e => setTrademark(e.target.value)}
              className="mt-1 rounded-lg border border-border-default bg-app px-3 py-2 text-sm text-text-primary"
              placeholder="опц."
            />
          </Field>
          <Field label="Дата від">
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="mt-1 rounded-lg border border-border-default bg-app px-3 py-2 text-sm text-text-primary"
            />
          </Field>
          <Field label="Дата до">
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="mt-1 rounded-lg border border-border-default bg-app px-3 py-2 text-sm text-text-primary"
            />
          </Field>
          <Field label="Охоплення">
            <select
              value={scope}
              onChange={e => setScope(e.target.value)}
              className="mt-1 rounded-lg border border-border-default bg-app px-3 py-2 text-sm text-text-primary"
            >
              {SCOPES.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </Field>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => load()}
              disabled={loading || !canLoad}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Показати
            </button>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-xs text-text-muted">
              <input
                type="checkbox"
                checked={includeSamples}
                onChange={e => setIncludeSamples(e.target.checked)}
                className="h-4 w-4 rounded border-border-default"
              />
              Приклади декларацій
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-text-muted">
              <input
                type="checkbox"
                checked={includeUndated}
                onChange={e => setIncludeUndated(e.target.checked)}
                className="h-4 w-4 rounded border-border-default"
              />
              Включати без дати
            </label>
            <button
              type="button"
              onClick={() => {
                setDateFrom(ALL_PERIOD_FROM);
                setDateTo(todayInput());
              }}
              className="rounded-md border border-border-default px-2 py-1 text-xs font-semibold text-text-secondary hover:bg-hover hover:text-text-primary"
            >
              Весь період
            </button>
            <button
              type="button"
              onClick={() => {
                setDateFrom(yearAgoInput());
                setDateTo(todayInput());
              }}
              className="rounded-md border border-border-default px-2 py-1 text-xs font-semibold text-text-secondary hover:bg-hover hover:text-text-primary"
            >
              Останній рік
            </button>
          </div>
          {data?.quality && (
            <div className="text-xs text-text-muted">
              {data.quality.scope} · схожість {data.quality.min_similarity} · max {fmtN(data.quality.max_candidates)}
              {data.quality.include_undated ? ' · без дати включено' : ''}
            </div>
          )}
        </div>

        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        )}
      </div>

      {loading && (
        <div className="rounded-xl border border-border-default bg-surface p-8 text-center text-text-muted">
          <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-blue-500" />
          Завантажую практику оформлення…
        </div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat label="Кандидати" value={fmtN(sample.candidate_count)} sub="до фільтра БД" />
            <Stat label="У вибірці" value={fmtN(sample.pool_raw_count)} sub={`без дати ${fmtN(sample.pool_undated_count)}`} tone="blue" />
            <Stat label="Різні оформлення" value={fmtN(sample.pool_cluster_count)} sub="без повторів" tone="green" />
            <Stat label="Домінуючий код" value={signals.dominant_code ? <HSCode code={signals.dominant_code} size="sm" /> : '—'} sub={fmtPct(signals.dominant_code_pool_raw_share)} tone="amber" />
            <Stat label="Свіжість" value={fmtPct(signals.fresh_share)} sub={`${sample.date_from} — ${sample.date_to}`} />
          </div>

          {warnings.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4" />
              {warnings.map(code => (
                <span key={code} className="rounded-md border border-amber-200 bg-surface px-2 py-0.5 dark:border-amber-900/60 dark:bg-amber-950/50">
                  {WARNING_LABELS[code] || code}
                </span>
              ))}
            </div>
          )}

          {byCode.length === 0 ? (
            <div className="rounded-xl border border-border-default bg-surface p-8 text-center text-text-muted">
              Немає декларацій для цих умов.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
              <CodeTable rows={byCode} selectedCode={selectedCode} onSelect={setSelectedCode} />
              <div className="space-y-4">
                <Samples codeRow={selectedRow} />
                <div className="rounded-lg border border-border-default bg-surface p-3">
                  <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-text-primary">
                    <Database className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    Сигнали вибірки
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg bg-app p-2">
                      <div className="text-text-disabled">Кодів</div>
                      <div className="font-mono text-text-primary">{fmtN(signals.code_count)}</div>
                    </div>
                    <div className="rounded-lg bg-app p-2">
                      <div className="text-text-disabled">Повтор кластера</div>
                      <div className="font-mono text-text-primary">{fmtPct(signals.largest_duplicate_cluster_share)}</div>
                    </div>
                    <div className="rounded-lg bg-app p-2">
                      <div className="text-text-disabled">Топ імпортер</div>
                      <div className="font-mono text-text-primary">{fmtPct(signals.top_importer_share)}</div>
                    </div>
                    <div className="rounded-lg bg-app p-2">
                      <div className="text-text-disabled">Свіжі</div>
                      <div className="font-mono text-text-primary">{fmtPct(signals.fresh_share)}</div>
                    </div>
                    <div className="rounded-lg bg-app p-2">
                      <div className="text-text-disabled">Без дати</div>
                      <div className="font-mono text-text-primary">{fmtPct(sample.pool_undated_share)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <SliceList title="Країни" rows={data.by_country} keyName="country" />
            <SliceList title="Марки" rows={data.by_trademark} keyName="trademark" />
            <SliceList title="Імпортери" rows={data.by_importer} keyName="importer_name" />
          </div>

          {selectedRow?.price_median != null && (
            <div className="rounded-lg border border-border-default bg-surface px-3 py-2 text-sm text-text-secondary">
              Типова ціна по вибраному коду: <b className="text-text-primary">{fmtPrice(selectedRow.price_median)}/кг</b>
            </div>
          )}
        </>
      )}

      {!loading && !data && !error && (
        <div className="rounded-xl border border-border-default bg-surface p-8 text-center text-text-muted">
          <BarChart3 className="mx-auto mb-2 h-7 w-7 text-text-disabled" />
          Дані практики з'являться після запиту.
        </div>
      )}
    </div>
  );
}
